import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import { parcelsApis } from '../apis';
import { ErrorHelpers, ParcelHelpers } from '../helpers';

const initialState = {
  status: 'idle',
  data: {},
};

const fetchParcelCodeByParcelNumber = createAsyncThunk(
  'parcels/fetchParcelCodeByParcelNumber',
  async ({ parcelNumber, postcodeOrOrderId }, { rejectWithValue }) => {
    try {
      const response = await parcelsApis.getParcelCodeByParcelNumber({
        parcelNumber,
        ...ParcelHelpers.getPostcodeOrOrderId(postcodeOrOrderId),
      });

      return response?.data;
    } catch (error) {
      if (error.response) {
        if (ErrorHelpers.isBadRequestError(error.response.data.error)) {
          return rejectWithValue(ErrorHelpers.formatParcelError(error));
        }

        return rejectWithValue(error.response.data.error);
      } else {
        throw error;
      }
    }
  }
);

const fetchParcelByNumber = createAsyncThunk(
  'parcel/fetchByNumber',
  async (parcelNumber, { rejectWithValue }) => {
    try {
      const response = await parcelsApis.getParcelByNumber(parcelNumber);

      return response.data;
    } catch (error) {
      if (error.response) {
        if (ErrorHelpers.isBadRequestError(error.response.data.error)) {
          return rejectWithValue(ErrorHelpers.formatParcelError(error));
        }

        return rejectWithValue(error.response.data.error);
      } else {
        throw error;
      }
    }
  }
);

const fetchParcelByCode = createAsyncThunk(
  'parcel/fetchByCode',
  async ({ parcelCode, postcodeOrOrderId }, { rejectWithValue }) => {
    try {
      const response = await parcelsApis.getParcelByCode({
        parcelCode,
        ...ParcelHelpers.getPostcodeOrOrderId(postcodeOrOrderId),
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        if (ErrorHelpers.isBadRequestError(error.response.data.error)) {
          return rejectWithValue(ErrorHelpers.formatParcelError(error));
        }

        return rejectWithValue(error.response.data.error);
      } else {
        throw error;
      }
    }
  }
);

const slice = createSlice({
  name: 'parcel',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchParcelByNumber.fulfilled, (state, { payload }) => {
        state.data[ParcelHelpers.normalizeParcelNumber(payload.parcelNumber)] =
          payload;
        state.status = 'success';
      })
      .addCase(fetchParcelByCode.fulfilled, (state, { payload }) => {
        state.data[ParcelHelpers.normalizeParcelNumber(payload.parcelCode)] =
          payload;
        state.status = 'success';
      });
  },
});

const getParcelState = state => state.parcel;

const getParcel = parcelNumber =>
  createSelector(getParcelState, state => ({
    data: state.data[parcelNumber],
    status: state.status,
  }));

export default {
  reducer: slice.reducer,
  actions: {
    fetchParcelCodeByParcelNumber,
    fetchParcelByNumber,
    fetchParcelByCode,
  },
  selectors: {
    getParcel,
  },
};
